/**
 * « Se tester » sur un pack (`/student/progression/test/$packId`, migration
 * 0336) : 2 questions par SKILLCARD du pack (15 au plus) → résultat par carte,
 * la plus fragile en tête. On évalue : XP seulement. Payant : sans les cartes
 * du pack (ni abonnement / pass), renvoi vers la page d'achat du pack.
 * Un test en cours se reprend ; « Arrêter » ramène à « Mon année ».
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ClipboardCheck, Lock, PauseCircle, RotateCw } from 'lucide-react';
import {
  Button,
  FeedbackPopup,
  MarkdownMath,
  MathText,
  computeXpReward,
  type XpReward,
} from '@jeraisonne/ui';
import { ChoiceButton } from '@/components/exercise/choice-button';
import { PaperChoices, PaperColumn, PaperPage, usePaperPlayerForMe } from '@/features/paper-player';
import { freeSheetKey } from '@/features/scratch-sheet/use-free-sheet';
import { QuestionVisuals } from '@/components/exercise/question-visuals';
import { Spinner } from '@/components/spinner';
import { isNativeApp } from '@/lib/platform';
import { resumeIndex } from '@/lib/skill-diagnostic';
import { packBuyPath } from '@/lib/skillcards/purchase';
import {
  PackTestError,
  finishPackTest,
  packTestCardsToRework,
  packTestErrorMessage,
  startPackTest,
  submitPackTestAnswer,
  type PackTestPlay,
  type PackTestResult,
} from '@/lib/pack-tests';

const BACK = '/student/progression';

type Phase = 'loading' | 'playing' | 'finishing' | 'result' | 'locked' | 'error';

export function PackTestPage() {
  const { packId } = useParams({ strict: false }) as { packId: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [phase, setPhase] = useState<Phase>('loading');
  const [play, setPlay] = useState<PackTestPlay | null>(null);
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  // Exercice « sur la feuille de brouillon » (drapeau `paper_player`).
  const paper = usePaperPlayerForMe();
  const [revealed, setRevealed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PackTestResult | null>(null);
  const [streak, setStreak] = useState(0);
  const [sessionXp, setSessionXp] = useState(0);
  const [reward, setReward] = useState<{
    value: XpReward;
    wasCorrect: boolean;
    explanationMd?: string;
    errorFeedbackMd?: string;
  } | null>(null);
  const shownAt = useRef(Date.now());
  const started = useRef(false);

  function refresh() {
    void qc.invalidateQueries({ queryKey: ['pack-tests-state'] });
  }

  function fail(e: unknown) {
    if (e instanceof PackTestError && e.code === 'LOCKED') {
      setPhase('locked');
      return;
    }
    setError(packTestErrorMessage(e instanceof PackTestError ? e.code : ''));
    setPhase('error');
  }

  async function finish(testId: string) {
    setPhase('finishing');
    try {
      setResult(await finishPackTest(testId));
      setPhase('result');
      refresh();
      void qc.invalidateQueries({ queryKey: ['student-profile'] });
    } catch (e) {
      fail(e);
    }
  }

  async function begin(restart: boolean) {
    setPhase('loading');
    setError(null);
    try {
      const p = await startPackTest(packId, restart);
      setPlay(p);
      setStreak(0);
      setSessionXp(0);
      const i = resumeIndex(p);
      if (i >= p.questions.length) {
        await finish(p.session.id);
        return;
      }
      setIndex(i);
      setSelected(null);
      setRevealed(false);
      shownAt.current = Date.now();
      setPhase('playing');
      refresh();
    } catch (e) {
      fail(e);
    }
  }

  // Le test démarre (ou reprend) dès l'ouverture : le bouton était sur le pack.
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void begin(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const question = play?.questions[index];

  async function answer(choiceId: string) {
    if (!play || !question || revealed || submitting) return;
    setSelected(choiceId);
    setSubmitting(true);
    try {
      const r = await submitPackTestAnswer({
        testId: play.session.id,
        questionId: question.step_id,
        choiceId,
        timeMs: Date.now() - shownAt.current,
      });
      setRevealed(true);
      const xp = computeXpReward(r.was_correct, streak);
      setStreak(xp.streak);
      setSessionXp((t) => t + xp.total);
      setReward({
        value: xp,
        wasCorrect: r.was_correct,
        explanationMd: r.explanation_md ?? undefined,
        errorFeedbackMd: r.feedback_md ?? undefined,
      });
    } catch (e) {
      fail(e);
    } finally {
      setSubmitting(false);
    }
  }

  function next() {
    if (!play) return;
    if (index < play.questions.length - 1) {
      setIndex((i) => i + 1);
      setSelected(null);
      setRevealed(false);
      shownAt.current = Date.now();
      return;
    }
    void finish(play.session.id);
  }

  if (phase === 'loading' || phase === 'finishing') {
    return (
      <Shell>
        <p role="status" className="text-ink-500 flex items-center gap-2 text-sm">
          <Spinner className="h-4 w-4" />
          {phase === 'finishing' ? 'Calcul de ton résultat…' : 'Préparation de ton test…'}
        </p>
      </Shell>
    );
  }

  if (phase === 'locked') {
    return (
      <Shell>
        <PackTestLocked packId={packId} />
      </Shell>
    );
  }

  if (phase === 'error') {
    return (
      <Shell>
        <p role="alert" className="text-error-700 bg-error-50 rounded-xl px-4 py-3 text-sm">
          {error}
        </p>
        <Button className="mt-4" fullWidth onClick={() => void navigate({ to: BACK })}>
          Retour à mon année
        </Button>
      </Shell>
    );
  }

  if (phase === 'result' && result) {
    return (
      <Shell>
        <PackTestResultView
          result={result}
          onAgain={() => void begin(true)}
          onBack={() => void navigate({ to: BACK })}
        />
      </Shell>
    );
  }

  if (!play || !question) return null;
  const total = play.questions.length;
  return (
    <Shell>
      <div className="mb-4 space-y-2">
        <div className="text-ink-600 flex items-center justify-between gap-2 text-xs font-semibold">
          <span className="flex items-center gap-1">
            <ClipboardCheck className="h-3.5 w-3.5" aria-hidden /> S’entraîner
          </span>
          <span>
            Question {index + 1} / {total}
          </span>
        </div>
        <div className="bg-ink-100 h-1.5 overflow-hidden rounded-full">
          <div
            className="bg-primary-500 h-full rounded-full transition-all"
            style={{ width: `${((index + 1) / total) * 100}%` }}
          />
        </div>
        {question.card_title && (
          <p className="text-ink-500 truncate text-xs">
            <MathText>{question.card_title}</MathText>
          </p>
        )}
      </div>
      {paper ? (
        <PaperPage className="-mx-4 min-h-0 sm:rounded-2xl">
          <PaperColumn className="pt-3">
            <div className="text-ink-900 mb-3 text-base">
              <MarkdownMath>{question.prompt_md}</MarkdownMath>
            </div>
            <div className="pp-figure">
              <QuestionVisuals visuals={question.visuals} />
            </div>
            <PaperChoices
              questionKey={question.step_id}
              promptMd={question.prompt_md}
              choices={question.choices}
              selected={selected}
              revealed={revealed}
              busy={submitting}
              onChoose={(id) => void answer(id)}
              storageKey={freeSheetKey('pack-test', play?.session.id, question.step_id)}
            />
          </PaperColumn>
        </PaperPage>
      ) : (
        <>
          <div className="text-ink-900 mb-4 text-base">
            <MarkdownMath>{question.prompt_md}</MarkdownMath>
          </div>
          <QuestionVisuals visuals={question.visuals} />
          <div className="space-y-2">
            {question.choices.map((choice, i) => (
              <ChoiceButton
                key={choice.id}
                choice={choice}
                index={i}
                selected={selected === choice.id}
                revealed={revealed}
                onClick={() => void answer(choice.id)}
              />
            ))}
          </div>
        </>
      )}
      <Button
        variant="ghost"
        fullWidth
        className="mt-6"
        onClick={() => {
          refresh();
          void navigate({ to: BACK });
        }}
      >
        <PauseCircle className="h-4 w-4" aria-hidden /> Arrêter, je reprendrai plus tard
      </Button>
      {reward && (
        <FeedbackPopup
          reward={reward.value}
          wasCorrect={reward.wasCorrect}
          sessionXp={sessionXp}
          explanationMd={reward.explanationMd}
          errorFeedbackMd={reward.errorFeedbackMd}
          onClose={() => {
            setReward(null);
            next();
          }}
        />
      )}
    </Shell>
  );
}

export function PackTestLocked({ packId }: { packId: string }) {
  const native = isNativeApp();
  return (
    <div className="space-y-4 text-center">
      <span className="bg-primary-50 ocean:bg-white/10 text-primary-600 mx-auto flex h-14 w-14 items-center justify-center rounded-2xl">
        <Lock className="h-7 w-7" aria-hidden />
      </span>
      <h1 className="font-display text-ink-900 text-xl font-extrabold">S’entraîner sur ce pack</h1>
      <p className="text-ink-600 text-sm">
        Le test d’un pack est réservé aux élèves qui ont débloqué ses SKILLCARDS (le pack, le
        programme ou toutes ses cartes), ou qui ont un abonnement.
      </p>
      {!native ? (
        <a
          href={packBuyPath(packId, BACK)}
          className="bg-primary-600 hover:bg-primary-700 inline-flex min-h-[2.75rem] w-full items-center justify-center rounded-xl px-5 text-sm font-semibold text-white active:scale-[0.98]"
        >
          Débloquer le pack
        </a>
      ) : (
        <p className="text-ink-500 text-xs">Le pack se débloque sur jeraisonne.fr.</p>
      )}
    </div>
  );
}

export function PackTestResultView({
  result,
  onAgain,
  onBack,
}: {
  result: PackTestResult;
  onAgain: () => void;
  onBack: () => void;
}) {
  const s = result.session;
  const toRework = packTestCardsToRework(result.cards);
  return (
    <div className="space-y-5">
      <div className="text-center">
        <p className="text-ink-500 text-xs font-bold uppercase tracking-wider">
          {result.pack?.title ?? 'S’entraîner'}
        </p>
        <p className="font-display text-ink-900 text-4xl font-extrabold">{s.score_pct ?? 0}%</p>
        <p className="text-ink-500 text-sm">
          {s.correct ?? 0} / {s.total ?? 0} bonnes réponses · +{s.xp_awarded} XP
        </p>
        <p className="text-ink-600 mt-1 text-sm">
          {toRework.length === 0
            ? 'Tout est réussi : ce pack est bien en place !'
            : `${toRework.length} SKILLCARD${toRework.length > 1 ? 'S' : ''} à retravailler.`}
        </p>
      </div>
      {result.cards.length > 0 && (
        <section className="space-y-2">
          <h2 className="font-display text-ink-900 text-base font-bold">Par SKILLCARD</h2>
          <ul className="space-y-2">
            {result.cards.map((c) => (
              <li key={c.card_id}>
                <Link
                  to="/card/$cardId"
                  params={{ cardId: c.card_id }}
                  className="border-ink-200 ocean:bg-white/5 flex min-h-[2.75rem] items-center justify-between gap-3 rounded-xl border bg-white px-4 py-2 text-sm active:scale-[0.99]"
                >
                  <span className="text-ink-900 min-w-0 font-semibold">
                    <MathText>{c.title ?? 'SKILLCARD'}</MathText>
                  </span>
                  <span
                    className={
                      c.correct === c.total
                        ? 'text-success-600 shrink-0 text-xs font-semibold'
                        : 'text-accent-600 shrink-0 text-xs font-semibold'
                    }
                  >
                    {c.correct}/{c.total}
                    {c.correct < c.total ? ' · à retravailler' : ''}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
      <div className="flex flex-col gap-2">
        <Button fullWidth onClick={onAgain}>
          <RotateCw className="h-4 w-4" aria-hidden /> M’entraîner à nouveau
        </Button>
        <Button variant="ghost" fullWidth onClick={onBack}>
          Retour à mon année
        </Button>
      </div>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-lg px-4 py-6 pb-24 sm:py-8 lg:max-w-2xl">
      <Link
        to={BACK}
        className="text-primary-600 mb-4 inline-flex min-h-[2.75rem] items-center gap-1 text-sm font-semibold"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden /> Mon année
      </Link>
      {children}
    </div>
  );
}
